/**
 * 계약 및 리스크 관리 컬럼 정의
 * CUL_BASIC 화면 전용 컬럼 정의
 */

import { CreateColumn } from '../../../../Services/agGrid/createColumn';
import {
  Box,
  Typography,
  Chip,
  LinearProgress,
  Button,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Remove,
  Visibility,
} from '@mui/icons-material';
import {
  getContractStatusChipSx,
  USAGE_RATE_FORMULA_NOTE,
} from './CUL_BASIC_StatusLegend';

// 유틸리티 함수들
const formatNumber = (num) => Number(num || 0).toLocaleString('ko-KR');

const getCompanyInitials = (name) => {
  if (!name) return '?';
  const words = name.split(' ');
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const getDaysRemaining = (endDate) => {
  if (!endDate) return '';
  const end = new Date(endDate);
  const now = new Date();
  const diffTime = end - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return '만료됨';
  if (diffDays === 0) return '오늘 만료';
  if (diffDays <= 30) return `${diffDays}일 후 만료`;
  if (diffDays <= 90) return `${Math.floor(diffDays / 30)}개월 남음`;
  if (diffDays <= 365) return `${Math.floor(diffDays / 30)}개월 남음`;
  return `${(diffDays / 365).toFixed(1)}년 남음`;
};

const getStatusColor = (renewalRate, daysRemaining) => {
  if (daysRemaining <= 30) return 'error';
  if (renewalRate >= 70) return 'success';
  if (renewalRate >= 30) return 'warning';
  return 'error';
};

const getStatusLabel = (renewalRate, daysRemaining) => {
  if (daysRemaining <= 30) return '위험';
  if (renewalRate >= 70) return '양호';
  if (renewalRate >= 30) return '주의';
  return '위험';
};

/** 회사 상세 분석 모달 열기 — 점 메뉴 대신 라벨 버튼 */
const CompanyDetailActionCell = ({ params, onViewDetail }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      py: 0.25,
      px: 0.5,
    }}
  >
    <Button
      size="small"
      variant="outlined"
      startIcon={<Visibility sx={{ fontSize: 16 }} />}
      onClick={(event) => {
        event.stopPropagation();
        if (onViewDetail && params.data) {
          onViewDetail(params.data);
        }
      }}
      sx={{
        fontSize: 12,
        fontWeight: 700,
        textTransform: 'none',
        borderColor: 'rgba(19,127,236,0.45)',
        color: '#137fec',
        whiteSpace: 'nowrap',
        px: 1.25,
        '&:hover': {
          borderColor: '#137fec',
          bgcolor: 'rgba(19,127,236,0.06)',
        },
      }}
    >
      상세 보기
    </Button>
  </Box>
);

/**
 * 계약 목록 컬럼 정의 생성
 * @param {Function} onViewDetail - 회사 상세 보기 핸들러
 * @returns {Array} 컬럼 정의 배열
 */
export const getColumnDefs = (onViewDetail) => {
  return [
    // 업체명 컬럼
    CreateColumn(
      'companyName',
      '업체명',
      'left',
      250,
      false,
      false,
      false,
      'custom',
      {
        cellRenderer: (params) => {
          const contract = params.data;
          if (!contract) return null;

          return (
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 12,
                  flexShrink: 0,
                }}
              >
                {getCompanyInitials(contract.companyName)}
              </Box>
              <Box>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25 }}
                >
                  {contract.companyName || '-'}
                </Typography>
                <Typography
                  sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.25 }}
                >
                  ID: #{contract.companyId || '-'}
                </Typography>
              </Box>
            </Box>
          );
        },
        flex: 1,
        minWidth: 250,
      }
    ),

    // 계약 종료일 컬럼
    CreateColumn(
      'contractEndDate',
      '계약 종료일',
      'left',
      200,
      false,
      false,
      false,
      'custom',
      {
        cellRenderer: (params) => {
          const contract = params.data;
          if (!contract) return null;

          const renewalRate = Number(contract.renewalRate || 0);
          const daysRemaining =
            contract.remainingDays !== undefined &&
            contract.remainingDays !== null
              ? Number(contract.remainingDays)
              : contract.contractEndDate
                ? (() => {
                    // 날짜 문자열을 파싱 (예: "2026년 06월 01일" -> "2026-06-01")
                    const dateStr = contract.contractEndDate
                      .replace(/년/g, '-')
                      .replace(/월/g, '-')
                      .replace(/일/g, '')
                      .replace(/\s+/g, '')
                      .split('-')
                      .map((part, idx) =>
                        idx === 0 ? part : part.padStart(2, '0')
                      )
                      .join('-');

                    const endDate = new Date(dateStr);
                    if (isNaN(endDate.getTime())) return null;
                    const now = new Date();
                    return Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                  })()
                : null;

          const daysText =
            daysRemaining !== null
              ? daysRemaining < 0
                ? '만료됨'
                : daysRemaining === 0
                  ? '오늘 만료'
                  : daysRemaining <= 30
                    ? `${daysRemaining}일 후 만료`
                    : daysRemaining <= 90
                      ? `${Math.floor(daysRemaining / 30)}개월 남음`
                      : daysRemaining <= 365
                        ? `${Math.floor(daysRemaining / 30)}개월 남음`
                        : `${(daysRemaining / 365).toFixed(1)}년 남음`
              : contract.contractEndDate
                ? getDaysRemaining(contract.contractEndDate)
                : '';

          const isExpiringSoon = daysRemaining !== null && daysRemaining <= 30;

          return (
            <Box sx={{ py: 0.25 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  lineHeight: 1.25,
                  fontWeight: isExpiringSoon ? 700 : 400,
                  color: isExpiringSoon ? 'error.main' : 'text.primary',
                }}
              >
                {contract.contractEndDate || '-'}
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  lineHeight: 1.25,
                  color: isExpiringSoon ? 'error.main' : 'text.secondary',
                }}
              >
                {daysText}
              </Typography>
            </Box>
          );
        },
        flex: 1,
        minWidth: 200,
      }
    ),

    // 활성 사용자 컬럼
    CreateColumn(
      'activeUsers',
      '활성 사용자 (30일)',
      'left',
      200,
      false,
      false,
      false,
      'custom',
      {
        cellRenderer: (params) => {
          const contract = params.data;
          if (!contract) return null;

          const activeUsers = Number(contract.activeUsers || 0);
          const userChangePercent = Number(contract.userChangePercent || 0);

          return (
            <Box sx={{ py: 0.25 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 0.5,
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                  {formatNumber(activeUsers)}
                </Typography>
                {userChangePercent !== 0 && (
                  <Chip
                    label={`${userChangePercent > 0 ? '+' : ''}${userChangePercent}%`}
                    size="small"
                    icon={
                      userChangePercent > 0 ? (
                        <TrendingUp sx={{ fontSize: 12 }} />
                      ) : userChangePercent < 0 ? (
                        <TrendingDown sx={{ fontSize: 12 }} />
                      ) : (
                        <Remove sx={{ fontSize: 12 }} />
                      )
                    }
                    sx={{
                      height: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor:
                        userChangePercent > 0
                          ? 'rgba(16,185,129,0.1)'
                          : userChangePercent < 0
                            ? 'rgba(239,68,68,0.1)'
                            : 'rgba(148,163,184,0.1)',
                      color:
                        userChangePercent > 0
                          ? '#10b981'
                          : userChangePercent < 0
                            ? '#ef4444'
                            : 'text.secondary',
                      '& .MuiChip-icon': {
                        fontSize: 12,
                        marginLeft: 0.5,
                      },
                    }}
                  />
                )}
              </Box>
              <Box
                sx={{
                  width: 96,
                  height: 4,
                  bgcolor: 'rgba(148,163,184,0.2)',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    bgcolor:
                      activeUsers > 100
                        ? '#10b981'
                        : activeUsers > 50
                          ? '#3b82f6'
                          : '#ef4444',
                    width: `${Math.min((activeUsers / 250) * 100, 100)}%`,
                  }}
                />
              </Box>
            </Box>
          );
        },
        flex: 1,
        minWidth: 200,
      }
    ),

    // 갱신 확률 컬럼
    CreateColumn(
      'renewalRate',
      '갱신 확률',
      'left',
      180,
      false,
      false,
      false,
      'custom',
      {
        cellRenderer: (params) => {
          const contract = params.data;
          if (!contract) return null;

          const renewalRate = Number(contract.renewalRate || 0);

          return (
            <Box sx={{ width: 140, py: 0.25 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color:
                      renewalRate >= 70
                        ? 'primary.main'
                        : renewalRate >= 30
                          ? 'warning.main'
                          : 'error.main',
                  }}
                >
                  {renewalRate}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={renewalRate}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: 'rgba(148,163,184,0.2)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor:
                      renewalRate >= 70
                        ? '#137fec'
                        : renewalRate >= 30
                          ? '#fbbf24'
                          : '#ef4444',
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          );
        },
        flex: 1,
        minWidth: 180,
      }
    ),

    // 상태 컬럼
    CreateColumn(
      'status',
      '상태',
      'center',
      132,
      false,
      false,
      false,
      'custom',
      {
        headerTooltip: USAGE_RATE_FORMULA_NOTE,
        cellRenderer: (params) => {
          const contract = params.data;
          if (!contract) return null;

          const statusLabel = String(contract.status || '').trim() || '-';
          const chipSx = getContractStatusChipSx(statusLabel);

          return (
            <Box sx={{ py: 0.25 }}>
              <Chip
                label={statusLabel}
                size="small"
                sx={{
                  height: 24,
                  fontSize: 12,
                  fontWeight: 700,
                  ...chipSx,
                }}
              />
            </Box>
          );
        },
        flex: 1,
        minWidth: 132,
      }
    ),

    CreateColumn(
      'monthlyContractFee',
      '계약금(월)',
      'right',
      130,
      false,
      false,
      false,
      'text',
      {
        minWidth: 120,
        valueFormatter: (params) => {
          const v = params.value;
          if (v === '' || v === null || v === undefined) return '-';
          const n = Number(v);
          if (Number.isNaN(n)) return String(v);
          return `${n.toLocaleString('ko-KR')}원`;
        },
      }
    ),

    CreateColumn(
      'paymentDay',
      '결제일(매월)',
      'center',
      160,
      false,
      false,
      false,
      'text',
      {
        minWidth: 150,
        valueFormatter: (params) => {
          const v = params.value;
          if (v === '' || v === null || v === undefined) return '-';
          const n = Number(v);
          if (!Number.isFinite(n)) return String(v);
          return `매월 ${n}일`;
        },
      }
    ),

    CreateColumn('action', '상세', 'center', 108, false, false, false, 'custom', {
      cellRenderer: (params) => (
        <CompanyDetailActionCell params={params} onViewDetail={onViewDetail} />
      ),
      sortable: false,
      filter: false,
      resizable: false,
      width: 108,
      minWidth: 108,
      maxWidth: 120,
      pinned: 'right',
    }),
  ];
};

const columnDefs = {
  getColumnDefs,
};

export default columnDefs;
